import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import '@polymer/polymer/lib/elements/dom-repeat.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {OrderableBehavior} from '../behaviors/orderable-behavior.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';

class KeliChecklist extends ScopeBinding(OrderableBehavior(HotkeyBehavior(PolymerElement))) {
  static get template() {
    return html`
<style>
  :host {
    display: block;
  }
  #caption {
    font-size: 1.1em;
    font-weight: 300;
    margin: 0 0 4px 0;
  }

  #caption sl-icon {
    vertical-align: middle;
    font-size: 20px;
  }

  .item {
    cursor: grab;
    position: relative;
    padding: 4px;
    border-radius: 3px;
    display: flex;
    flex-direction: row;
  }

  .item-toggle {
    width: 26px;
  }

  #add {
    padding-left: 30px;
  }

  #add #add-fields {
    display: none;
  }

  #add.adding #add-placeholder {
    display: none;
  }

  #add.adding #add-fields {
    display: block;
  }

  #add-placeholder {
    cursor: pointer;
    margin: 0;
    padding: 4px 0;
    font-size: .9em;
    color: var(--keli-text-subtle);
  }

  #addCaption {
    display: block;
  }

  #addSubmit {
    width: auto;
    display: inline-block;
    margin: 0 .5em 0 0;
  }

  #add-cancel {
    cursor: pointer;
    font-size: .9em;
    text-decoration: underline;
    color: var(--keli-text-subtle);
  }

  .item keli-checkbox {
    min-height: 0;
    padding: 0;
  }

  .item-caption[completed] {
    text-decoration: line-through;
    color: var(--keli-text-subtle);
  }

  #progress {
    position: relative;
    margin-bottom: 4px;
    --height: 4px;
    --sl-border-radius-pill: none;
    --indicator-color: var(--keli-progress-color);
  }

  #progress-percent {
    position: absolute;
    width: 30px;
    font-size: .7em;
    display: inline-block;
    text-align: center;
    margin-top: -2px;
  }

  #progress-bar {
    position: relative;
    margin-left: 30px;
    width: auto;
  }

  .orderable-ghost {
    cursor: grabbing;
    background-color: var(--keli-subtle-background);
  }
</style>

<keli-checklist-controller id="controller" keliname="_t"></keli-checklist-controller>

<template is="dom-if" if="[[caption]]">
  <h1 id="caption">
    <sl-icon name="playlist-add-check"></sl-icon>
    [[caption]]
  </h1>
</template>
<div id="progress">
  <span id="progress-percent">[[progressPercent]]%</span>
  <sl-progress-bar id="progress-bar" value="[[progressPercent]]"></sl-progress-bar>
</div>
<div id="checklist">
  <template is="dom-repeat" id="checklistItemsRepeat" items="[[checklistItems]]">
    <div id="[[item.id]]" class="item">
      <div class="item-toggle">
        <keli-checkbox display-value="[[item.textStatus]]" rowid="[[item.id]]"></keli-checkbox>
      </div>
      <div class="item-caption" on-dblclick="handleItemActivate" completed$="[[item.status]]">[[item.caption]]</div>
    </div>
  </template>
</div>
<div id="add">
  <p id="add-placeholder" on-click="handleAddItemClick">Add an item&hellip;</p>
  <div id="add-fields">
    <keli-input id="addCaption" no-caption></keli-input>
    <keli-button id="addSubmit" caption="Add" background="a700"></keli-button>
    <span id="add-cancel" on-click="handleAddItemCancel">Cancel</span>
  </div>
</div>
`;
  }

  static get is() {
    return 'keli-checklist';
  }

  static get properties() {
    return {
      caption: {type: String, scopevar: true},
      checklistItems: {type: Array, observer: 'checklistItemsChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['checklist']},
      needsDataLoaded: {type: Boolean, value: true},
      progressPercent: {type: Number, value: 0}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliChecklistItemActivated']);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.addEventListener('keliActionActivated', this.handleKeliActionActivated);
    this.addEventListener('keliUserChange', this.handleKeliUserChange);
    this.addEventListener('panelElementCreated', this.handlePanelElementCreated);
    this.addEventListener('orderable-end', this.handleOrderableEnd);

    this.orderableElement = this.$.checklist;
    this.orderableSelector = '.item';
    this.$.addSubmit.focusOnActivation = false;

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.checklist.save, {
      down: event => {
        event.preventDefault();
        event.stopPropagation();
        this.$.addSubmit.activate(event);
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.checklist.cancel, {
      down: event => {
        if (this.$.add.classList.contains('adding')) {
          event.preventDefault();
          event.stopPropagation();
          this.handleAddItemCancel();
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.removeEventListener('keliActionActivated', this.handleKeliActionActivated);
    this.removeEventListener('keliUserChange', this.handleKeliUserChange);
    this.removeEventListener('panelElementCreated', this.handlePanelElementCreated);
    this.removeEventListener('orderable-end', this.handleOrderableEnd);
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  checklistItemsChanged(newValue) {
    if (Array.isArray(newValue)) {
      let completed = 0;
      if (newValue.length) {
        completed = newValue.reduce((accumulator, value) => {
          return accumulator + (value.status ? 1 : 0);
        }, 0);
      }
      if (completed) {
        this.progressPercent = Math.floor((completed / newValue.length) * 100);
      } else {
        this.progressPercent = 0;
      }
    } else {
      this.progressPercent = 0;
    }
  }

  handleOrderableEnd(event) {
    if (typeof event.detail.newIndex === 'undefined' || event.detail.newIndex === event.detail.oldIndex) {
      return;
    }
    if (event.detail.newIndex >= this.checklistItems.length) {
      let k = event.detail.newIndex - this.checklistItems.length;
      while ((k--) + 1) {
        this.checklistItems.push(undefined);
      }
    }
    this.checklistItems.splice(event.detail.newIndex, 0, this.checklistItems.splice(event.detail.oldIndex, 1)[0]);

    this.$.controller.updateChecklistOrder(this.checklistItems);
  }

  handleAddItemClick() {
    this.$.add.classList.add('adding');
    this.$.addCaption.dispatchEvent(new CustomEvent('focusElement'));
  }

  handleAddItemCancel() {
    this.$.add.classList.remove('adding');
  }

  handleSetPanelActiveElement(event) {
    event.stopPropagation();
    this.defaultHotkeyListener && resetHotkeysForListener(this.defaultHotkeyListener);
  }

  handleKeliActionActivated(event) {
    event.stopPropagation();
    this.addNewItem();
  }

  handleKeliUserChange(event) {
    event.stopPropagation();

    if (event.detail.element.tagName === 'KELI-CHECKBOX') {
      let rowId = event.detail.element.rowid,
        newStatus = (event.detail.newValue ? 'Y' : 'N');
      this.$.controller.updateStatus(rowId, newStatus);

      for (let i = 0; i < this.checklistItems.length; ++i) {
        if (this.checklistItems[i].id === rowId) {
          this.set(['checklistItems', i, 'status'].join('.'), event.detail.newValue);
          break;
        }
      }
      this.checklistItemsChanged(this.checklistItems);
    }
  }

  handlePanelElementCreated(event) {
    event.stopPropagation();
  }

  handleItemActivate(event) {
    this.dispatchEvent(new CustomEvent('keliChecklistItemActivated', {
      detail: {
        id: event.currentTarget.parentElement.getAttribute('id')
      }
    }));
  }

  addNewItem() {
    let itemCaption = this.$.addCaption.currentValue;
    if (!itemCaption) {
      return;
    }

    this.$.controller.saveNewItem(itemCaption).then((newId) => {
      this.push('checklistItems', {id: newId, caption: itemCaption});
    });

    this.$.addCaption.currentValue = '';
  }
}

customElements.define(KeliChecklist.is, KeliChecklist);
export {KeliChecklist};
